// MMO 공유 필드 HUD(REBIRTH-MODES-SPEC 4.2, 레인 M): 채널·인원, 월드 보스 체력 바·남은 시간·등장 알림, 처치 기여도 결과.
// DOM은 처음에 한 번 만들고, update()는 값이 바뀐 칸만 고친다(글자 비교 — 프레임마다 DOM을 다시 만들지 않는다).
// 오프라인이면 채널 칸에 '혼자 모험 중'만 보인다(게임은 막지 않는다).
import { BOSS_TIER_LABEL, BossPhase, FIELD_BOSS } from '../shared/fieldNet';
import type { FieldBossView, FieldNetStatus, FieldResultView } from '../game/fieldNet';
import { C, FONT, FONT_NUM } from './dom';

/** HUD가 읽는 FieldNet의 부분(테스트·QA가 가짜를 넣을 수 있게) */
export interface FieldNetHudSource {
  readonly status: FieldNetStatus;
  readonly online: boolean;
  readonly channel: number;
  readonly count: number;
  readonly boss: FieldBossView;
  readonly result: FieldResultView;
}

/** 남은 시간 글자(mm:ss, 1시간 이상은 h:mm:ss) */
export function clockText(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  const mm = (h ? String(m).padStart(2, '0') : String(m)) + ':' + String(r).padStart(2, '0');
  return h ? h + ':' + mm : mm;
}

/** 채널 칸 글자 */
export function channelText(src: Pick<FieldNetHudSource, 'status' | 'online' | 'channel' | 'count'>): string {
  if (src.online) return '채널 ' + src.channel + ' · ' + src.count + '명';
  if (src.status === 'connecting' || src.status === 'retrying') return '필드 서버 연결 중…';
  return '혼자 모험 중';
}

/** 보스 칸 상태 글자(바 아래 줄) */
export function bossLine(b: FieldBossView, wallNow: number): string {
  if (b.phase === BossPhase.Alive) return '도주까지 ' + clockText(b.endsAt - wallNow);
  if (b.phase === BossPhase.Dead) return '처치! 다음 등장 ' + clockText(b.nextAt - wallNow);
  if (b.phase === BossPhase.Escaped) return '도주했다 · 다음 등장 ' + clockText(b.nextAt - wallNow);
  return b.nextAt > 0 ? '월드 보스 등장까지 ' + clockText(b.nextAt - wallNow) : '';
}

export class FieldNetHud {
  readonly root: HTMLDivElement;
  private readonly chan: HTMLDivElement;
  private readonly bossBox: HTMLDivElement;
  private readonly bossName: HTMLDivElement;
  private readonly bossFill: HTMLDivElement;
  private readonly bossPct: HTMLDivElement;
  private readonly bossSub: HTMLDivElement;
  private readonly notice: HTMLDivElement;
  private readonly resultBox: HTMLDivElement;
  private lastChan = '';
  private lastSub = '';
  private lastPct = -1;
  private lastPhase = -1;
  private bossSerial = -1;
  private resultSerial = 0;
  private noticeUntil = 0;
  private resultUntil = 0;

  constructor(parent: HTMLElement) {
    const root = document.createElement('div');
    root.dataset.testid = 'field-net-hud';
    root.style.cssText = 'position:absolute;inset:0;pointer-events:none;font-family:' + FONT + ';color:#f3f1ea;';
    this.root = root;
    // 자리(2026-10-28 E2E): 게임 HUD 왼쪽 위 영웅 판(~64px) 밑에 채널 칩, 가운데 위 연계 안내(~32px) 밑에 보스 바
    this.chan = div(root, 'field-channel', 'position:absolute;top:70px;left:10px;padding:4px 10px;border-radius:14px;background:rgba(16,22,42,.78);border:1px solid rgba(242,193,78,.55);font-size:13px;font-weight:700;letter-spacing:.02em;');
    this.bossBox = div(root, 'field-boss', 'position:absolute;top:40px;left:50%;transform:translateX(-50%);width:min(400px,52vw);display:none;text-align:center;');
    this.bossName = div(this.bossBox, 'field-boss-name', 'font-size:15px;font-weight:900;color:' + C.gold + ';text-shadow:0 2px 0 #000,0 0 8px rgba(0,0,0,.6);margin-bottom:3px;');
    const bar = div(this.bossBox, '', 'position:relative;height:16px;border-radius:8px;background:rgba(10,12,24,.85);border:2px solid ' + C.gold + ';overflow:hidden;box-shadow:0 2px 0 rgba(0,0,0,.5);');
    this.bossFill = div(bar, 'field-boss-fill', 'position:absolute;left:0;top:0;bottom:0;width:100%;background:linear-gradient(180deg,#ff6b5a,' + C.crimson + ' 60%,#a3121f);transition:width .12s linear;');
    this.bossPct = div(bar, 'field-boss-pct', 'position:absolute;inset:0;font:700 11px/16px ' + FONT_NUM + ';color:#fff;text-shadow:0 1px 0 #000;');
    this.bossSub = div(this.bossBox, 'field-boss-sub', 'margin-top:3px;font-size:12px;color:#c9d3ef;text-shadow:0 1px 0 #000;');
    this.notice = div(root, 'field-boss-notice', 'position:absolute;top:22%;left:50%;transform:translateX(-50%);padding:10px 22px;border-radius:12px;background:rgba(40,8,14,.86);border:2px solid ' + C.crimson + ';font-size:20px;font-weight:900;color:#ffe9b0;text-shadow:0 2px 0 #000;display:none;white-space:nowrap;');
    this.resultBox = div(root, 'field-boss-result', 'position:absolute;top:30%;left:50%;transform:translateX(-50%);min-width:min(320px,86vw);padding:12px 16px;border-radius:12px;background:rgba(16,22,42,.92);border:2px solid ' + C.gold + ';display:none;font-size:13px;');
    parent.appendChild(root);
  }

  /** 4Hz~60Hz 아무 때나 불러도 된다. wallNow = Date.now(), nowMs = performance.now() */
  update(src: FieldNetHudSource, wallNow: number, nowMs: number): void {
    const ch = channelText(src);
    if (ch !== this.lastChan) { this.lastChan = ch; this.chan.textContent = ch; }
    const b = src.boss;
    if (b.serial !== this.bossSerial) {
      this.bossSerial = b.serial;
      if (b.phase === BossPhase.Alive && this.lastPhase !== BossPhase.Alive && this.lastPhase !== -1) {
        this.notice.textContent = '월드 보스 「' + FIELD_BOSS.name + '」 등장!';
        this.notice.style.display = 'block';
        this.noticeUntil = nowMs + 4000;
      }
      this.lastPhase = b.phase;
      const show = b.phase !== BossPhase.None || b.nextAt > 0;
      this.bossBox.style.display = show ? 'block' : 'none';
      this.bossName.textContent = b.phase === BossPhase.None ? '월드 보스' : FIELD_BOSS.name;
      const pct = b.phase === BossPhase.Alive ? Math.max(0, Math.min(1, b.hp / Math.max(1, b.maxHp))) : b.phase === BossPhase.Dead ? 0 : b.phase === BossPhase.None ? 1 : b.hp / Math.max(1, b.maxHp);
      if (pct !== this.lastPct) {
        this.lastPct = pct;
        this.bossFill.style.width = (pct * 100).toFixed(2) + '%';
        this.bossPct.textContent = b.phase === BossPhase.Alive ? b.hp.toLocaleString('ko-KR') + ' / ' + b.maxHp.toLocaleString('ko-KR') : '';
      }
    }
    const sub = bossLine(b, wallNow);
    if (sub !== this.lastSub) { this.lastSub = sub; this.bossSub.textContent = sub; }
    if (this.noticeUntil && nowMs > this.noticeUntil) { this.noticeUntil = 0; this.notice.style.display = 'none'; }
    const r = src.result;
    if (r.serial !== this.resultSerial) {
      this.resultSerial = r.serial;
      this.renderResult(r);
      this.resultUntil = nowMs + 12_000;
    }
    if (this.resultUntil && nowMs > this.resultUntil) { this.resultUntil = 0; this.resultBox.style.display = 'none'; }
  }

  private renderResult(r: FieldResultView): void {
    const box = this.resultBox;
    box.replaceChildren();
    div(box, '', 'font-size:16px;font-weight:900;color:' + C.gold + ';text-align:center;margin-bottom:6px;', FIELD_BOSS.name + ' 처치 · 기여도');
    for (let i = 0; i < r.count; i++) {
      const row = r.rows[i];
      div(box, 'field-result-row', 'display:flex;justify-content:space-between;gap:12px;padding:2px 0;',
        row.rank + '위  ' + row.name + '   ' + (row.ratio * 100).toFixed(1) + '%   [' + BOSS_TIER_LABEL[row.tier] + ']');
    }
    const me = r.meRank > 0
      ? '내 순위 ' + r.meRank + '/' + r.total + ' · ' + (r.meRatio * 100).toFixed(1) + '% · 등급 ' + BOSS_TIER_LABEL[r.meTier]
      : '이번 보스에 피해를 주지 못했다';
    div(box, 'field-result-me', 'margin-top:6px;padding-top:6px;border-top:1px solid rgba(242,193,78,.4);font-weight:800;text-align:center;', me);
    div(box, '', 'margin-top:4px;font-size:11px;color:#9fb0d8;text-align:center;', '보상은 계정 기록에 남았다(지급 연결 예정 · 게스트는 보상 없음)');
    box.style.display = 'block';
  }

  dispose(): void { this.root.remove(); }
}

function div(parent: HTMLElement, testid: string, css: string, text?: string): HTMLDivElement {
  const d = document.createElement('div');
  if (testid) d.dataset.testid = testid;
  d.style.cssText = css;
  if (text !== undefined) d.textContent = text;
  parent.appendChild(d);
  return d;
}
